import {
  BoneProperties,
  RotationLimits,
  type Bone,
} from './core';

export type RotationLimitsAxisMode = 'unlimited' | 'limited' | 'locked';

export const DEFAULT_LIMITED_MIN_DEGREES = -180;
export const DEFAULT_LIMITED_MAX_DEGREES = 180;

function normalizeLimitValue(value: number | null): number | null {
  if (value === null) return null;
  return Number.isFinite(value) ? value : null;
}

export function getRotationLimitsAxisMode(
  limits: RotationLimits,
  axis: number,
): RotationLimitsAxisMode {
  if (limits.locked[axis]) return 'locked';
  if (limits.min[axis] !== null || limits.max[axis] !== null) return 'limited';
  return 'unlimited';
}

export function ensureBoneProperties(bone: Bone): BoneProperties {
  if (!bone.properties) bone.properties = new BoneProperties();
  return bone.properties;
}

export function ensureRotationLimits(bone: Bone): RotationLimits {
  const properties = ensureBoneProperties(bone);
  if (!properties.rotationLimits) {
    properties.rotationLimits = new RotationLimits();
  }
  return properties.rotationLimits;
}

export function setRotationLimitsAxisMode(
  limits: RotationLimits,
  axis: number,
  mode: RotationLimitsAxisMode,
): void {
  if (mode === 'locked') {
    limits.locked[axis] = true;
    limits.min[axis] = null;
    limits.max[axis] = null;
    return;
  }

  limits.locked[axis] = false;

  if (mode === 'limited') {
    limits.min[axis] = limits.min[axis] ?? DEFAULT_LIMITED_MIN_DEGREES;
    limits.max[axis] = limits.max[axis] ?? DEFAULT_LIMITED_MAX_DEGREES;
    return;
  }

  limits.min[axis] = null;
  limits.max[axis] = null;
}

export function setRotationLimitMinValue(
  limits: RotationLimits,
  axis: number,
  value: number | null,
): void {
  limits.min[axis] = normalizeLimitValue(value);
}

export function setRotationLimitMaxValue(
  limits: RotationLimits,
  axis: number,
  value: number | null,
): void {
  limits.max[axis] = normalizeLimitValue(value);
}

export function setRotationLimitRange(
  limits: RotationLimits,
  axis: number,
  min: number | null,
  max: number | null,
): void {
  limits.locked[axis] = false;
  limits.min[axis] = normalizeLimitValue(min);
  limits.max[axis] = normalizeLimitValue(max);
}

export function getRotationLimitRange(
  limits: RotationLimits,
  axis: number,
): { min: number | null; max: number | null } {
  return { min: limits.min[axis], max: limits.max[axis] };
}

export function pruneBonePropertiesDefaults(bone: Bone): void {
  const properties = bone.properties;
  if (!properties) return;

  if (properties.rotationLimits && !properties.rotationLimits.hasAny) {
    properties.rotationLimits = null;
  }

  const preferredAngles = properties.preferredAngles;
  if (
    preferredAngles &&
    preferredAngles.x === 0 &&
    preferredAngles.y === 0 &&
    preferredAngles.z === 0
  ) {
    properties.preferredAngles = null;
  }

  if (!properties.rotationLimits && !properties.preferredAngles) {
    bone.properties = null;
  }
}
